import { SearchIcon } from '@chakra-ui/icons';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  useColorModeValue,
  Center,
  Spinner,
  Box,
  Text,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Td,
  Badge,
  Tbody,
  Collapse,
  Tooltip,
  useDisclosure,
  UnorderedList,
  ListItem,
  Spacer,
  InputGroup,
  InputRightElement,
  Input,
} from '@chakra-ui/react';
import IdTag from 'components/common/fields/IdTag';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { GiMagnifyingGlass } from 'react-icons/gi';
import { useSelector } from 'react-redux';
import {
  baseSubQuestColumns,
  interactiveSubQuestColumns,
  quizSubQuestColumns,
  videoSubQuestColumns,
  RemoveProgressionButton,
} from 'utils/columns/reports/progressionColumns';
import { ReducerStatus } from 'utils/constants';
import {
  COMPLETED,
  IN_PROGRESS,
  NOT_STARTED,
} from 'utils/constants/reportStatus';
import {
  INTERACTIVE_ID,
  QUIZ_ID,
  VIDEO_ID,
} from 'utils/constants/subQuestType';

function ClassSubQuestProgressMd({ subQuestId, paramSearch, isOpen, onClose }) {
  const records = useSelector((state) => state.class.records);
  const getClassRecords = useSelector((state) => state.class.getClassRecords);

  const { isOpen: isOpenUserList, onToggle: onShowUser } = useDisclosure();

  const [currentSubQuest, setCurrentSubQuest] = useState(null);
  const [filterText, setFilterText] = useState(paramSearch || '');
  const [columns, setColumns] = useState([]);
  const [subQuestRecords, setSubQuestRecords] = useState([]);

  const filteredData = subQuestRecords.filter(
    (item) =>
      (item.id &&
        item.id.toString().toLowerCase().includes(filterText.toLowerCase())) ||
      (item.ownerName &&
        item.ownerName.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.courseName &&
        item.courseName.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.questName &&
        item.questName.toLowerCase().includes(filterText.toLowerCase()))
  );

  useEffect(() => {
    const questSubQuest = records.questSubQuests.find(
      (qs) => qs.subQuest.id === subQuestId
    );
    if (questSubQuest) setCurrentSubQuest(questSubQuest.subQuest);

    setSubQuestRecords(
      records.progressionRecords.filter((pr) => pr.subQuestId === subQuestId)
    );
  }, [subQuestId, records]);

  useEffect(() => {
    if (!currentSubQuest) return;
    switch (currentSubQuest.type.id) {
      case VIDEO_ID:
        setColumns(videoSubQuestColumns);
        break;
      case QUIZ_ID:
        setColumns(quizSubQuestColumns);
        break;
      case INTERACTIVE_ID:
        setColumns(interactiveSubQuestColumns);
        break;
      default:
        setColumns(baseSubQuestColumns);
        break;
    }
  }, [currentSubQuest]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex wrap="wrap" align="center" gap={2}>
            <IdTag id={subQuestId} />
            {currentSubQuest?.name}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text as="samp" fontSize="xs">
            <strong> REPORT SUMMARY</strong>
          </Text>

          <TableContainer mt={4}>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>type</Th>
                  <Th>percentage</Th>
                  <Th isNumeric>count</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>
                    <Badge colorScheme="green">{COMPLETED}</Badge>
                  </Td>
                  <Td>
                    {Math.round(
                      (records.progressionRecords.filter(
                        (pr) =>
                          pr.subQuestId === subQuestId &&
                          pr.dateFinished !== null
                      ).length /
                        records.users.length) *
                        100
                    )}
                    %
                  </Td>
                  <Td isNumeric>
                    {
                      records.progressionRecords.filter(
                        (pr) =>
                          pr.subQuestId === subQuestId &&
                          pr.dateFinished !== null
                      ).length
                    }
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Badge colorScheme="blue">{IN_PROGRESS}</Badge>
                  </Td>
                  <Td>
                    {Math.round(
                      (records.progressionRecords.filter(
                        (pr) =>
                          pr.subQuestId === subQuestId &&
                          pr.dateFinished === null
                      ).length /
                        records.users.length) *
                        100
                    )}
                    %
                  </Td>
                  <Td isNumeric>
                    {
                      records.progressionRecords.filter(
                        (pr) =>
                          pr.subQuestId === subQuestId &&
                          pr.dateFinished === null
                      ).length
                    }
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Badge colorScheme="gray">{NOT_STARTED}</Badge>
                  </Td>
                  <Td>
                    {Math.round(
                      (records.users.filter(
                        (user) =>
                          !records.progressionRecords
                            .filter((pr) => pr.subQuestId === subQuestId)
                            .map((record) => record.ownerId)
                            .includes(user.id)
                      ).length /
                        records.users.length) *
                        100
                    )}
                    %
                  </Td>
                  <Td isNumeric>
                    <Tooltip label="View">
                      <Button
                        rightIcon={<GiMagnifyingGlass />}
                        onClick={onShowUser}
                        colorScheme="blue"
                        variant="outline"
                        size="xs"
                      >
                        {
                          records.users.filter(
                            (user) =>
                              !records.progressionRecords
                                .filter((pr) => pr.subQuestId === subQuestId)
                                .map((record) => record.ownerId)
                                .includes(user.id)
                          ).length
                        }
                      </Button>
                    </Tooltip>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>

          <Collapse in={isOpenUserList} animateOpacity mt={4}>
            <Box mt={5}>
              <Text as="samp" fontSize="xs">
                <strong> USERS WHO HAVEN&apos;T STARTED THIS ACTIVITY</strong>
              </Text>

              <UnorderedList mt={3} fontSize="sm" pl={4}>
                {records.users
                  .filter(
                    (user) =>
                      !records.progressionRecords
                        .filter((pr) => pr.subQuestId === subQuestId)
                        .map((record) => record.ownerId)
                        .includes(user.id)
                  )
                  .map((user) => (
                    <ListItem key={user.id}>{user.name}</ListItem>
                  ))}
              </UnorderedList>
            </Box>
          </Collapse>

          <Box
            w="100%"
            p={4}
            my={5}
            color="gray.500"
            borderWidth="1px"
            borderRadius="lg"
          >
            <Flex m={2} gap={2} align="center">
              <Spacer />
              <InputGroup width={{ base: '100%', md: '350px' }}>
                <InputRightElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputRightElement>
                <Input
                  name="query"
                  placeholder="Search"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </InputGroup>
            </Flex>

            <DataTable
              theme={useColorModeValue('default', 'dark')}
              progressPending={getClassRecords.status === ReducerStatus.LOADING}
              columns={[...columns, RemoveProgressionButton()]}
              data={filteredData}
              progressComponent={
                <Center my={10}>
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="blue.500"
                    size="xl"
                  />
                </Center>
              }
              pagination
            />
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

ClassSubQuestProgressMd.defaultProps = {
  subQuestId: null,
  paramSearch: '',
};

ClassSubQuestProgressMd.propTypes = {
  subQuestId: PropTypes.number,
  paramSearch: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ClassSubQuestProgressMd;
